import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { RefObject } from "preact";
import { boardLag, foldCoast, foldFree, foldHintOpen, foldHintShows, foldKeep, foldOf, foldSnap, foldStart } from "../shared/board-fold.ts";
import { scrollingToTop } from "./scroll-to-top";

/**
 * The fold on the web's open board (shared/board-fold.ts has the rules; docs/notes.md "The fold").
 * The board rests with Next up at the top and the notice board above it: `main.content` starts at
 * the fold before the first paint, a pull down from it reveals the board trailing the scroll, and
 * once the gesture is over (the finger up and the scroll settled) the shared rule sends it to the
 * top or back to the fold. Below the fold nothing snaps.
 *
 * The browser's own scroll snapping is not used: `proximity` snaps to the nearest point (half the
 * board, not 40 percent) and pulls a list stopped near Next up back to it, which the list must not
 * do. So the decision is ours, on `scrollend` or a short quiet after the last scroll, and only after
 * a touch or a wheel: a scroll made by the app itself (a restore, the tab's back to top, the tour)
 * never snaps. With no touch at all (a mouse; foldFree) nothing snaps or lags either: the board
 * starts folded, then the wheel and the keys scroll freely, like any page.
 *
 * `wrap` is the notice board's wrapper (`.notice-fold`), the scroller's first child. Off (the Done
 * board, notes off), it leaves the scroller exactly as it was.
 *
 * Returns the fold's hint (shared/board-fold.ts foldHintShows, foldHintOpen): `hint` whenever there is
 * a fold; `open` while the notice board is revealed (the add button steps aside; the arrow itself never
 * turns, owner, 2026-10-05: "Remove the rotating arrow"); and `reveal`, the tap, which scrolls to the
 * top the way the fold rule does.
 */
export function useBoardFold(
  contentRef: RefObject<HTMLElement>,
  wrapRef: RefObject<HTMLElement>,
  on: boolean,
  ringing: boolean,
): { hint: boolean; open: boolean; reveal: () => void } {
  const state = useRef({ fold: 0, board: 0 });
  const ringingRef = useRef(ringing);
  ringingRef.current = ringing;
  const [hint, setHint] = useState(false);
  const [open, setOpen] = useState(false);
  // Set by the effect below while the fold is on: end any gesture, then scroll to the top.
  const revealRef = useRef<() => void>(() => {});

  // Measure and start at the fold before the first paint; follow the board's height after.
  useLayoutEffect(() => {
    const root = contentRef.current;
    const wrap = wrapRef.current;
    if (!on || !root || !wrap) return;
    const s = state.current;
    const gapOf = () => parseFloat(getComputedStyle(root).rowGap) || 12;
    const apply = (fold: number) => {
      s.fold = fold;
      root.style.setProperty("--fold", `${fold}px`);
      root.dataset.foldTop = String(fold);
    };
    root.classList.add("has-fold");
    s.board = wrap.offsetHeight;
    apply(foldOf(s.board, gapOf()));
    root.scrollTop = foldStart({ fold: s.fold, ringing: ringingRef.current });
    // No touch (a mouse and keys): nothing snaps and nothing lags; the wheel moves freely (foldFree).
    const free = foldFree({ coarse: window.matchMedia?.("(pointer: coarse)").matches ?? false, touch: "ontouchstart" in window });
    root.dataset.foldFree = free ? "1" : "0";
    // The arrow keeps its last resting reading through a pull, so it never flickers (foldHintOpen).
    let opened = false;
    const showHint = (resting = false) => {
      setHint(foldHintShows(root.scrollTop, s.fold));
      opened = foldHintOpen({ y: root.scrollTop, fold: s.fold, was: opened, resting, free });
      setOpen(opened);
    };
    showHint(true);

    let touching = false;
    let gesture = false;
    let from = s.fold;
    let release = 0;
    let released = NaN;
    let lastY = root.scrollTop;
    let lastT = performance.now();
    let velocity = 0;
    let timer = 0;
    let frame = 0;
    const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    // While a slip is open or the composer is, the board is being used: nothing snaps away from it.
    const held = () => !!wrap.querySelector(".note-composer, .slip.open");

    const drawLag = () => {
      frame = 0;
      const lag = free || reduced() || held() ? 0 : boardLag(root.scrollTop, s.fold, s.board);
      wrap.style.transform = lag > 0 ? `translateY(${-lag}px)` : "";
    };
    // The decision the moment the finger lifts (shared/board-fold.ts foldSnap): the move cancels a
    // coast, so nothing floats half-revealed. Lifted below the fold, the gesture stays open for a
    // coast (foldCoast, in onScroll) until a quiet moment closes it (`final`).
    const decide = (final: boolean) => {
      window.clearTimeout(timer);
      timer = 0;
      if (touching || !gesture || scrollingToTop()) return;
      const y = root.scrollTop;
      const v = release;
      if (held()) {
        gesture = false;
        release = 0;
        return;
      }
      const to = foldSnap({ y, fold: s.fold, boardHeight: s.board, velocity: v, from, view: root.clientHeight, released });
      if (to !== null) {
        gesture = false;
        release = 0;
        root.scrollTo({ top: to, behavior: reduced() ? "auto" : "smooth" });
        return;
      }
      if (final) {
        gesture = false;
        release = 0;
        showHint(true);
      }
    };
    const onScrollEnd = () => {
      decide(true);
      if (!gesture && !touching) showHint(true);
    };
    const settleSoon = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => decide(true), 140);
    };
    const onScroll = () => {
      const now = performance.now();
      const y = root.scrollTop;
      showHint();
      const dt = now - lastT;
      if (dt > 0) velocity = (y - lastY) / dt;
      lastY = y;
      lastT = now;
      if (!frame) frame = requestAnimationFrame(drawLag);
      if (gesture && !touching) {
        // A coast climbing into the board after the finger lifted below the fold: back to the fold at once.
        const to = held() ? null : foldCoast({ y, fold: s.fold, released });
        if (to !== null) {
          gesture = false;
          release = 0;
          root.scrollTo({ top: to, behavior: reduced() ? "auto" : "smooth" });
          return;
        }
        settleSoon();
      }
    };
    const onTouchStart = () => {
      touching = true;
      gesture = true;
      from = root.scrollTop;
      released = NaN;
      velocity = 0;
      window.clearTimeout(timer);
    };
    const onTouchEnd = () => {
      touching = false;
      // A reading taken long before the finger left is no speed at all.
      release = performance.now() - lastT < 100 ? velocity : 0;
      released = root.scrollTop;
      decide(false);
      if (gesture) settleSoon();
    };
    const onWheel = () => {
      // Free, a wheel roll is only a scroll: no gesture, so no decision.
      if (free) return;
      if (!gesture) from = root.scrollTop;
      gesture = true;
      settleSoon();
    };
    // The hint's tap (its touch began a gesture on the scroller): no decision follows it, so the
    // fold rule's coast catch does not send the board straight back.
    const endGesture = () => {
      touching = false;
      gesture = false;
      release = 0;
      released = NaN;
      window.clearTimeout(timer);
      timer = 0;
    };
    revealRef.current = () => {
      endGesture();
      root.scrollTo({ top: 0, behavior: reduced() ? "auto" : "smooth" });
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchend", onTouchEnd, { passive: true });
    root.addEventListener("touchcancel", onTouchEnd, { passive: true });
    root.addEventListener("wheel", onWheel, { passive: true });
    root.addEventListener("scrollend", onScrollEnd);

    // The board grows or shrinks (a slip opened, a note pinned, the scroll arriving): the fold
    // follows, and a scroller at rest at the fold, or down the list, keeps what it showed.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => {
      const board = wrap.offsetHeight;
      if (board === s.board) return;
      const oldFold = s.fold;
      s.board = board;
      apply(foldOf(board, gapOf()));
      const to = foldKeep({ y: root.scrollTop, oldFold, newFold: s.fold, active: touching || timer !== 0 });
      if (to !== null) root.scrollTop = to;
      showHint();
      if (!frame) frame = requestAnimationFrame(drawLag);
    });
    observer?.observe(wrap);
    return () => {
      observer?.disconnect();
      window.clearTimeout(timer);
      cancelAnimationFrame(frame);
      root.removeEventListener("scroll", onScroll);
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchend", onTouchEnd);
      root.removeEventListener("touchcancel", onTouchEnd);
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("scrollend", onScrollEnd);
      root.classList.remove("has-fold");
      root.style.removeProperty("--fold");
      delete root.dataset.foldTop;
      delete root.dataset.foldFree;
      wrap.style.transform = "";
      s.fold = 0;
      revealRef.current = () => {};
      setHint(false);
      setOpen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  // A note that starts ringing for this person after the board showed brings the board into view,
  // unless the person is already down the list or scrolling.
  useEffect(() => {
    const root = contentRef.current;
    const s = state.current;
    if (!on || !ringing || !root || s.fold <= 0) return;
    if (root.scrollTop <= s.fold + 1 && root.scrollTop > 0) root.scrollTo({ top: 0, behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, ringing]);

  const reveal = useCallback(() => revealRef.current(), []);
  return { hint: on && hint, open: on && hint && open, reveal };
}
